'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth, useGetLeaderboardRank } from '@morfi/backend-sdk-v2/client';
import { AnimatePresence, domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { UserIdentity, type UserIdentityShape } from '@/components/profile/user-identity';
import { RankBadge } from '@/components/social/shared/rank-badge';
import { rankCellNumeralClassName } from '@/components/social/shared/rank-numeral';
import type { BackerSort, BoosterSort, CreatorSort, PnlSort, SocialTab } from '@/components/social/types';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Badge } from '@/components/ui/app-badge';
import { Icon } from '@/components/ui/icon';
import { Award01Icon, LinkForwardIconSolid } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { TOAST_IDS } from '@/config/toast-ids';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { formatCompactNumber } from '@/lib/format/format-number';
import { withDemoMode } from '@/lib/network';
import { useUserNetwork } from '@/lib/network/client';
import { LB_BACKERS_FIELDS, LB_BOOSTERS_FIELDS, LB_CREATORS_FIELDS, LB_TRADERS_FIELDS } from '@/lib/sdk/projections';
import { ShareModal } from '@/lib/share';
import { MOTION_ENTRANCE, MOTION_EXIT } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Floating "Your rank" bar for /social (spec 074).
//
// Renders one of three states:
//   1. hidden — unauthenticated, auth still loading, or all rank queries failed.
//   2. ranked — user has a rank in the active tab + active sort. Single-row
//      panel with avatar + identity + #rank + signed metric. Click → invokes
//      `onAnchorJump(userId)` which lifts the cursor in SocialClient.
//   3. cta — authenticated but no rank for this tab+sort. Persistent CTA
//      pointing to the qualifying action: PnL → /markets, Boosters & Backers →
//      /proposals, Creators → /proposals?create=1.
//
// Position:
//   • Mobile: hugs the bottom of the viewport at 8px — same offset as the
//     market-detail Trade CTA (the mobile dock is hidden on /social).
//   • Desktop (≥1024px): centred at bottom, capped at 640px width.
//
// Animation:
//   • <AnimatePresence mode="wait"> keyed on `${tab}-${variant}-${sort}` —
//     previous content fades + slides down before new content slides up.
//   • useReducedMotion() short-circuits to a snap.
// ---------------------------------------------------------------------------

// The rank bar reads the viewer's single unified row for the active board, via
// `useGetLeaderboardRank`. The `kind` discriminator uses `traders` where the tab
// is `leaderboard`; the `fields` reuse the same per-board projections as the
// list so the rank row stays in lockstep with the columns above it.
const TAB_TO_KIND: Record<SocialTab, 'traders' | 'boosters' | 'creators' | 'backers'> = {
  leaderboard: 'traders',
  boosters: 'boosters',
  creators: 'creators',
  backers: 'backers'
};
const FIELDS_BY_TAB: Record<SocialTab, readonly string[]> = {
  leaderboard: LB_TRADERS_FIELDS,
  boosters: LB_BOOSTERS_FIELDS,
  creators: LB_CREATORS_FIELDS,
  backers: LB_BACKERS_FIELDS
};
const ENTER_TRANSITION: typeof MOTION_ENTRANCE = MOTION_ENTRANCE;
const EXIT_TRANSITION: typeof MOTION_EXIT = MOTION_EXIT;
const ENTER_OFFSET: {
  readonly y: 12;
  readonly opacity: 0;
} = {
  y: 12,
  opacity: 0
} as const;
const VISIBLE: {
  readonly y: 0;
  readonly opacity: 1;
} = {
  y: 0,
  opacity: 1
} as const;
type ActiveSort = PnlSort | BoosterSort | CreatorSort | BackerSort;
interface SocialYourRankBarProps {
  activeTab: SocialTab;
  activeSort: ActiveSort;
  /** Currently-selected leaderboard window, so the viewer's rank matches the windowed list above it. */
  activeWindow: '1d' | '1w' | '1m' | '1y' | 'lifetime';
  onAnchorJump: (userId: string) => void;
}

/** Resolved rank-share config passed into <RankedRow>. Built by the parent
 *  on each tab when ALL the relevant ranks for that tab are positive. The
 *  surface kind drives which OG card the unfurl preview renders. */
type RankShareSurface = {
  kind: 'rank';
  userId: string;
  pnlRank: number;
  volumeRank: number;
} | {
  kind: 'rank_boosters';
  userId: string;
  amountRank: number;
  boostsRank: number;
  marketsBoostedRank: number;
} | {
  kind: 'rank_creators';
  userId: string;
  topMarketRank: number;
  marketsCreatedRank: number;
  totalVolumeRank: number;
} | {
  kind: 'rank_backers';
  userId: string;
  amountRank: number;
  backsRank: number;
  topMarketRank: number;
};

/** Maps the share surface to the `?board=` discriminator the portfolio
 *  SEO surface parses so the unfurl renders the matching rank OG card
 *  (instead of the default profile card). See `lib/seo/surfaces/profile.ts`. */
const RANK_BOARD_BY_KIND: Record<RankShareSurface['kind'], string> = {
  rank: 'traders',
  rank_boosters: 'boosters',
  rank_creators: 'creators',
  rank_backers: 'backers'
};
interface RankShareConfig {
  surface: RankShareSurface;
  url: string;
  text: string;
  modalTitle: string;
  ariaLabel: string;
  linkCopiedMessage: string;
}
interface BuildRankShareConfigArgs {
  userId: string;
  surface: RankShareSurface;
  network: 'mainnet' | 'devnet' | 'local';
  shareModalTitle: string;
  shareText: string;
  ariaLabel: string;
  linkCopiedMessage: string;
}
function buildRankShareConfig(args: BuildRankShareConfigArgs): RankShareConfig {
  // SSR-safe: window is undefined during SSR; we only need the URL on
  // the client where ShareModal actually renders the trigger. An empty
  // string here gets replaced as soon as the component hydrates.
  const url: string = ((): string => {
    if (typeof window === 'undefined') return '';
    const u = new URL(`/portfolio/${args.userId}`, window.location.origin);
    withDemoMode(u, args.network === 'mainnet' ? 'mainnet' : 'devnet');
    // The `?board=` discriminator makes the unfurl emit the tab's rank OG
    // card rather than the portfolio's default profile card — the profile
    // surface helper reads it in `generateMetadata`.
    u.searchParams.set('board', RANK_BOARD_BY_KIND[args.surface.kind]);
    return u.toString();
  })();
  return {
    surface: args.surface,
    url,
    text: args.shareText,
    modalTitle: args.shareModalTitle,
    ariaLabel: args.ariaLabel,
    linkCopiedMessage: args.linkCopiedMessage
  };
}

/** Internal — what to render given the tab + sort + rank payloads. The
 *  unauthenticated / loading "hidden" state is an early `return null` in the
 *  component, not a variant — `resolveRanked` only ever yields `ranked` or `cta`. */
type Variant = {
  kind: 'ranked';
  rank: number;
  /** Raw value to animate — a D6 string for currency modes, a count string
   *  for `plain`. Formatted (compact, no decimals) per-frame in RankedRow. */
  metricRawD6: string;
  metricMode: MetricMode;
  metricSign: 'pos' | 'neg' | 'neutral';
  identity: UserIdentityShape;
} | {
  kind: 'cta';
  tab: SocialTab;
};

/**
 * Local mirror of the SDK's identity sub-object — kept narrow because the
 * generated SDK types are deeply nested behind opaque codegen names.
 */
interface SdkUser {
  userId: string | null;
  walletAddress?: string;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
  avatarNsfw?: boolean | null;
  avatarPreferences?: {
    rasterise: boolean;
  } | null;
  connectedProviders?: ReadonlyArray<{
    provider: 'x' | 'google' | 'apple';
    username: string | null;
    displayName: string | null;
  }>;
}

/** The viewer's single unified rank row (superset of every board's fields). */
interface RankRow {
  user: SdkUser;
  totalPnl: string | null;
  totalVolume: string | null;
  totalTrades: number | null;
  marketsTraded: number | null;
  pnlRank: number | null;
  volumeRank: number | null;
  tradesRank: number | null;
  marketsRank: number | null;
  totalAmountBoosted: string | null;
  numBoosts: number | null;
  marketsBoosted: number | null;
  amountRank: number | null;
  boostsRank: number | null;
  marketsBoostedRank: number | null;
  topMarketVolume: string | null;
  topMarketRank: number | null;
  totalMarketsCreated: number | null;
  creatorTotalVolume: string | null;
  marketsCreatedRank: number | null;
  totalVolumeRank: number | null;
  totalBackedNetD6: string | null;
  numBacks: number | null;
  backsRank: number | null;
}
type MetricMode = 'signed' | 'usd' | 'plain';
interface Slot {
  rank: number | null;
  metric: string;
  mode: MetricMode;
}
function normaliseIdentity(raw: SdkUser): UserIdentityShape {
  return {
    userId: raw.userId,
    walletAddress: raw.walletAddress ?? '',
    displayName: raw.displayName,
    handle: raw.handle,
    avatarUrl: raw.avatarUrl,
    avatarNsfw: raw.avatarNsfw ?? null,
    avatarPreferences: raw.avatarPreferences ?? null,
    connectedProviders: (raw.connectedProviders ?? []).map(p => ({
      provider: p.provider,
      username: p.username ?? '',
      displayName: p.displayName
    }))
  };
}
function isPositiveRank(rank: number | null): boolean {
  return rank !== null && rank > 0;
}

/** Pick the active sort's rank + metric within the active tab from the one row. */
function slotForTab(activeTab: SocialTab, activeSort: ActiveSort, row: RankRow): Slot {
  if (activeTab === 'leaderboard') {
    const sortKey = activeSort as PnlSort;
    if (sortKey === 'pnl') return {
      rank: row.pnlRank,
      metric: row.totalPnl ?? '0',
      mode: 'signed'
    };
    if (sortKey === 'volume') return {
      rank: row.volumeRank,
      metric: row.totalVolume ?? '0',
      mode: 'usd'
    };
    if (sortKey === 'trades') return {
      rank: row.tradesRank,
      metric: String(row.totalTrades ?? 0),
      mode: 'plain'
    };
    return {
      rank: row.marketsRank,
      metric: String(row.marketsTraded ?? 0),
      mode: 'plain'
    };
  }
  if (activeTab === 'boosters') {
    const sortKey = activeSort as BoosterSort;
    if (sortKey === 'amount') return {
      rank: row.amountRank,
      metric: row.totalAmountBoosted ?? '0',
      mode: 'usd'
    };
    if (sortKey === 'boosts') return {
      rank: row.boostsRank,
      metric: String(row.numBoosts ?? 0),
      mode: 'plain'
    };
    return {
      rank: row.marketsBoostedRank,
      metric: String(row.marketsBoosted ?? 0),
      mode: 'plain'
    };
  }
  if (activeTab === 'creators') {
    const sortKey = activeSort as CreatorSort;
    if (sortKey === 'topMarket') return {
      rank: row.topMarketRank,
      metric: row.topMarketVolume ?? '0',
      mode: 'usd'
    };
    if (sortKey === 'marketsCreated') return {
      rank: row.marketsCreatedRank,
      metric: String(row.totalMarketsCreated ?? 0),
      mode: 'plain'
    };
    return {
      rank: row.totalVolumeRank,
      metric: row.creatorTotalVolume ?? '0',
      mode: 'usd'
    };
  }
  const sortKey = activeSort as BackerSort;
  if (sortKey === 'amount') return {
    rank: row.amountRank,
    metric: row.totalBackedNetD6 ?? '0',
    mode: 'usd'
  };
  if (sortKey === 'backs') return {
    rank: row.backsRank,
    metric: String(row.numBacks ?? 0),
    mode: 'plain'
  };
  return {
    rank: row.topMarketRank,
    metric: row.topMarketVolume ?? '0',
    mode: 'usd'
  };
}

/** Resolve the rank + metric for the active sort within the active tab. The
 *  metric is returned RAW (D6 string / count string) + its mode; RankedRow
 *  formats it compactly and animates it. */
function resolveRanked(activeTab: SocialTab, activeSort: ActiveSort, row: RankRow | undefined): Variant {
  if (row === undefined) return {
    kind: 'cta',
    tab: activeTab
  };
  const slot = slotForTab(activeTab, activeSort, row);
  if (!isPositiveRank(slot.rank)) return {
    kind: 'cta',
    tab: activeTab
  };
  const metricSign: 'pos' | 'neg' | 'neutral' = slot.mode === 'signed' ? Number(slot.metric) > 0 ? 'pos' : Number(slot.metric) < 0 ? 'neg' : 'neutral' : 'neutral';
  return {
    kind: 'ranked',
    rank: slot.rank ?? 0,
    metricRawD6: slot.metric,
    metricMode: slot.mode,
    metricSign,
    identity: normaliseIdentity(row.user)
  };
}

// Mobile bottom offset hugs the viewport at exactly 8px — matching the market-
// detail page's fixed Trade CTA (`mobile-trade-shell.tsx`). The mobile bottom
// nav is hidden on /social (see `isCoreMobileNavSurface`), so the bar does NOT
// need to clear a 4rem dock. 640px is a desktop readability cap, not a token.
//
// The bar is portaled to `document.body` (see `SocialYourRankBar` below) so
// its `position: fixed` + centre math resolve against the viewport. A
// transformed ancestor would otherwise become the containing block, so
// `left-1/2 -translate-x-1/2` would centre within that element (wrong)
// instead of the viewport.
const POSITIONING_CLASSES = 'fixed inset-x-3 bottom-[8px] z-overlay lg:bottom-4 lg:inset-x-auto lg:left-1/2 lg:max-w-[640px] lg:w-[min(640px,calc(100vw-2rem))] lg:-translate-x-1/2'; // ARBITRARY: 8px hugs the browser chrome to match the trade CTA; 640px desktop readability cap not expressible via spacing tokens.

export function SocialYourRankBar(t0) {
  const $ = _c(72);
  const {
    activeTab,
    activeSort,
    activeWindow,
    onAnchorJump
  } = t0;
  const t = useTranslations("social.your_rank_bar");
  const tLb = useTranslations("social.leaderboard");
  const tBoosters = useTranslations("social.boosters");
  const tCreators = useTranslations("social.creators");
  const tBackers = useTranslations("social.backers");
  const {
    isAuthenticated,
    isLoading,
    user
  } = useAuth();
  const prefersReducedMotion = useReducedMotion();
  const {
    network
  } = useUserNetwork();
  const [portalTarget, setPortalTarget] = useState(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setPortalTarget(document.body);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useLayoutEffect(t1, t2);
  const userId = user?.id ?? null;
  const enabled = isAuthenticated && userId !== null;
  const rankUserId = enabled && userId !== null ? userId : "";
  const t3 = TAB_TO_KIND[activeTab];
  const t4 = FIELDS_BY_TAB[activeTab];
  let t5;
  if ($[2] !== t4) {
    t5 = [...t4];
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== activeWindow || $[5] !== rankUserId || $[6] !== t3 || $[7] !== t5) {
    t6 = {
      kind: t3,
      userId: rankUserId,
      window: activeWindow,
      fields: t5
    };
    $[4] = activeWindow;
    $[5] = rankUserId;
    $[6] = t3;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const rankQuery = useGetLeaderboardRank(t6);
  const rankRow = rankQuery.rows[0] as RankRow | undefined;
  if (isLoading || !isAuthenticated || userId === null) {
    return null;
  }
  let t7;
  if ($[9] !== activeSort || $[10] !== activeTab || $[11] !== rankRow) {
    t7 = resolveRanked(activeTab, activeSort, rankRow);
    $[9] = activeSort;
    $[10] = activeTab;
    $[11] = rankRow;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const variant = t7;
  let t8;
  if ($[13] !== activeSort || $[14] !== activeTab || $[15] !== tBackers || $[16] !== tBoosters || $[17] !== tCreators || $[18] !== tLb) {
    t8 = activeTab === "leaderboard" ? tLb(activeSort as PnlSort === "pnl" ? "sort_pnl" : activeSort as PnlSort === "volume" ? "sort_volume" : activeSort as PnlSort === "trades" ? "trades" : "markets_traded") : activeTab === "boosters" ? tBoosters(activeSort as BoosterSort === "amount" ? "column_boost_volume" : activeSort as BoosterSort === "boosts" ? "column_boosts" : "column_markets") : activeTab === "creators" ? tCreators(activeSort as CreatorSort === "topMarket" ? "column_top_market" : activeSort as CreatorSort === "marketsCreated" ? "column_markets_created" : "column_total_volume") : tBackers(activeSort as BackerSort === "amount" ? "column_backed" : activeSort as BackerSort === "backs" ? "column_backs" : "column_top_market");
    $[13] = activeSort;
    $[14] = activeTab;
    $[15] = tBackers;
    $[16] = tBoosters;
    $[17] = tCreators;
    $[18] = tLb;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const metricLabel = t8;
  const isResolvingRank = !rankQuery.isFetched;
  let t9;
  bb0: {
    if (variant.kind !== "ranked" || rankRow === undefined) {
      t9 = null;
      break bb0;
    }
    if (activeTab === "leaderboard") {
      if (!isPositiveRank(rankRow.pnlRank) || !isPositiveRank(rankRow.volumeRank)) {
        t9 = null;
        break bb0;
      }
      const t10 = rankRow.pnlRank ?? 0;
      const t11 = rankRow.volumeRank ?? 0;
      let t12;
      if ($[20] !== network || $[21] !== t || $[22] !== t10 || $[23] !== t11 || $[24] !== userId) {
        t12 = buildRankShareConfig({
          userId,
          surface: {
            kind: "rank",
            userId,
            pnlRank: t10,
            volumeRank: t11
          },
          network,
          shareModalTitle: t("share_modal_title"),
          shareText: t("share_text"),
          ariaLabel: t("aria_share"),
          linkCopiedMessage: t("link_copied")
        });
        $[20] = network;
        $[21] = t;
        $[22] = t10;
        $[23] = t11;
        $[24] = userId;
        $[25] = t12;
      } else {
        t12 = $[25];
      }
      t9 = t12;
      break bb0;
    }
    if (activeTab === "boosters") {
      if (!isPositiveRank(rankRow.amountRank) || !isPositiveRank(rankRow.boostsRank) || !isPositiveRank(rankRow.marketsBoostedRank)) {
        t9 = null;
        break bb0;
      }
      const t10 = rankRow.amountRank ?? 0;
      const t11 = rankRow.boostsRank ?? 0;
      const t12 = rankRow.marketsBoostedRank ?? 0;
      let t13;
      if ($[26] !== network || $[27] !== t || $[28] !== t10 || $[29] !== t11 || $[30] !== t12 || $[31] !== userId) {
        t13 = buildRankShareConfig({
          userId,
          surface: {
            kind: "rank_boosters",
            userId,
            amountRank: t10,
            boostsRank: t11,
            marketsBoostedRank: t12
          },
          network,
          shareModalTitle: t("share_modal_title"),
          shareText: t("share_text_boosters"),
          ariaLabel: t("aria_share"),
          linkCopiedMessage: t("link_copied")
        });
        $[26] = network;
        $[27] = t;
        $[28] = t10;
        $[29] = t11;
        $[30] = t12;
        $[31] = userId;
        $[32] = t13;
      } else {
        t13 = $[32];
      }
      t9 = t13;
      break bb0;
    }
    if (activeTab === "creators") {
      if (!isPositiveRank(rankRow.topMarketRank) || !isPositiveRank(rankRow.marketsCreatedRank) || !isPositiveRank(rankRow.totalVolumeRank)) {
        t9 = null;
        break bb0;
      }
      const t10 = rankRow.topMarketRank ?? 0;
      const t11 = rankRow.marketsCreatedRank ?? 0;
      const t12 = rankRow.totalVolumeRank ?? 0;
      let t13;
      if ($[33] !== network || $[34] !== t || $[35] !== t10 || $[36] !== t11 || $[37] !== t12 || $[38] !== userId) {
        t13 = buildRankShareConfig({
          userId,
          surface: {
            kind: "rank_creators",
            userId,
            topMarketRank: t10,
            marketsCreatedRank: t11,
            totalVolumeRank: t12
          },
          network,
          shareModalTitle: t("share_modal_title"),
          shareText: t("share_text_creators"),
          ariaLabel: t("aria_share"),
          linkCopiedMessage: t("link_copied")
        });
        $[33] = network;
        $[34] = t;
        $[35] = t10;
        $[36] = t11;
        $[37] = t12;
        $[38] = userId;
        $[39] = t13;
      } else {
        t13 = $[39];
      }
      t9 = t13;
      break bb0;
    }
    if (activeTab === "backers") {
      if (!isPositiveRank(rankRow.amountRank) || !isPositiveRank(rankRow.backsRank) || !isPositiveRank(rankRow.topMarketRank)) {
        t9 = null;
        break bb0;
      }
      const t10 = rankRow.amountRank ?? 0;
      const t11 = rankRow.backsRank ?? 0;
      const t12 = rankRow.topMarketRank ?? 0;
      let t13;
      if ($[40] !== network || $[41] !== t || $[42] !== t10 || $[43] !== t11 || $[44] !== t12 || $[45] !== userId) {
        t13 = buildRankShareConfig({
          userId,
          surface: {
            kind: "rank_backers",
            userId,
            amountRank: t10,
            backsRank: t11,
            topMarketRank: t12
          },
          network,
          shareModalTitle: t("share_modal_title"),
          shareText: t("share_text_backers"),
          ariaLabel: t("aria_share"),
          linkCopiedMessage: t("link_copied")
        });
        $[40] = network;
        $[41] = t;
        $[42] = t10;
        $[43] = t11;
        $[44] = t12;
        $[45] = userId;
        $[46] = t13;
      } else {
        t13 = $[46];
      }
      t9 = t13;
      break bb0;
    }
    t9 = null;
  }
  const rankShare = t9;
  const reduceMotion = prefersReducedMotion === true;
  let t10;
  if ($[47] !== reduceMotion) {
    t10 = reduceMotion ? {
      duration: 0
    } : ENTER_TRANSITION;
    $[47] = reduceMotion;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  const transitionEnter = t10;
  reduceMotion ? {
    duration: 0
  } : EXIT_TRANSITION;
  const initial = reduceMotion ? false : ENTER_OFFSET;
  const exit = reduceMotion ? VISIBLE : ENTER_OFFSET;
  const presenceKey = variant.kind === "ranked" ? `${activeTab}-ranked-${String(activeSort)}` : `${activeTab}-cta`;
  if (portalTarget === null) {
    return null;
  }
  let t11;
  if ($[49] !== exit || $[50] !== initial || $[51] !== isResolvingRank || $[52] !== metricLabel || $[53] !== onAnchorJump || $[54] !== presenceKey || $[55] !== rankShare || $[56] !== t || $[57] !== transitionEnter || $[58] !== userId || $[59] !== variant.identity || $[60] !== variant.kind || $[61] !== variant.metricMode || $[62] !== variant.metricRawD6 || $[63] !== variant.metricSign || $[64] !== variant.rank || $[65] !== variant.tab) {
    t11 = !isResolvingRank && <m.div key={presenceKey} initial={initial} animate={VISIBLE} exit={exit} transition={transitionEnter}>{variant.kind === "ranked" ? <RankedRow rank={variant.rank} metricRawD6={variant.metricRawD6} metricMode={variant.metricMode} metricSign={variant.metricSign} metricLabel={metricLabel} identity={variant.identity} label={t("label")} actionLabel={t("action_view")} onClick={() => onAnchorJump(userId)} share={rankShare} /> : <CtaRow tab={variant.tab} text={variant.tab === "leaderboard" ? t("cta_pnl_text") : variant.tab === "boosters" ? t("cta_boosters_text") : variant.tab === "creators" ? t("cta_creators_text") : t("cta_backers_text")} action={variant.tab === "leaderboard" ? t("cta_pnl_action") : variant.tab === "boosters" ? t("cta_boosters_action") : variant.tab === "creators" ? t("cta_creators_action") : t("cta_backers_action")} />}</m.div>;
    $[49] = exit;
    $[50] = initial;
    $[51] = isResolvingRank;
    $[52] = metricLabel;
    $[53] = onAnchorJump;
    $[54] = presenceKey;
    $[55] = rankShare;
    $[56] = t;
    $[57] = transitionEnter;
    $[58] = userId;
    $[59] = variant.identity;
    $[60] = variant.kind;
    $[61] = variant.metricMode;
    $[62] = variant.metricRawD6;
    $[63] = variant.metricSign;
    $[64] = variant.rank;
    $[65] = variant.tab;
    $[66] = t11;
  } else {
    t11 = $[66];
  }
  let t12;
  if ($[67] !== t11) {
    t12 = <LazyMotion features={domAnimation}><div className={POSITIONING_CLASSES}><AnimatePresence mode="wait">{t11}</AnimatePresence></div></LazyMotion>;
    $[67] = t11;
    $[68] = t12;
  } else {
    t12 = $[68];
  }
  let t13;
  if ($[69] !== portalTarget || $[70] !== t12) {
    t13 = createPortal(t12, portalTarget);
    $[69] = portalTarget;
    $[70] = t12;
    $[71] = t13;
  } else {
    t13 = $[71];
  }
  return t13;
}
interface RankedRowProps {
  rank: number;
  metricRawD6: string;
  metricMode: MetricMode;
  metricSign: 'pos' | 'neg' | 'neutral';
  /** Active sort's column header (e.g. "Backed", "PnL") shown above the value. */
  metricLabel: string;
  identity: UserIdentityShape;
  label: string;
  actionLabel: string;
  onClick: () => void;
  /** When non-null, renders a share button to the right of the metric.
   *  Only set on the PnL leaderboard tab when the user has positive
   *  pnlRank + volumeRank — see parent's `buildRankShareConfig`. */
  share: RankShareConfig | null;
}
function RankedRow(t0) {
  const $ = _c(38);
  const {
    rank,
    metricRawD6,
    metricMode,
    metricSign,
    metricLabel,
    identity,
    label,
    actionLabel,
    onClick,
    share
  } = t0;
  const {
    formatD6,
    formatD6Signed
  } = useFormattedCurrency();
  const tRank = useTranslations("ui.data_table");
  let t1;
  if ($[0] !== formatD6 || $[1] !== formatD6Signed || $[2] !== metricMode) {
    t1 = v => {
      const rounded = Math.round(v);
      if (metricMode === "signed") {
        return formatD6Signed(String(rounded), "compactWhole");
      }
      if (metricMode === "usd") {
        return formatD6(String(rounded), "compactWhole");
      }
      return formatCompactNumber(rounded, 0);
    };
    $[0] = formatD6;
    $[1] = formatD6Signed;
    $[2] = metricMode;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const formatMetric = t1;
  let t2;
  if ($[4] !== rank || $[5] !== tRank) {
    t2 = <div className="flex min-w-7 shrink-0 items-center justify-center gap-1">{rank >= 1 && rank <= 3 ? <RankBadge rank={rank} size="display" table={true} trophyAriaLabel={tRank("rank_trophy_aria", {
        rank
      })} /> : <span className={cn(rankCellNumeralClassName(), "text-foreground-tertiary")}>{rank}</span>}</div>;
    $[4] = rank;
    $[5] = tRank;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== identity) {
    t3 = <UserIdentity user={identity} variant="with-avatar" size="sm" avatarSize="md" className="min-w-0" handleClassName="text-foreground-tertiary" nameClassName="text-body" showProviderBadges={true} />;
    $[7] = identity;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== label) {
    t4 = <Badge variant="secondary" className="shrink-0 text-micro">{label}</Badge>;
    $[9] = label;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="flex min-w-0 flex-1 items-center gap-2">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== metricLabel) {
    t6 = <span className="text-caption font-medium leading-none text-foreground-tertiary">{metricLabel}</span>;
    $[14] = metricLabel;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const t7 = Number(metricRawD6);
  const t8 = metricSign === "pos" && "text-data-profit";
  const t9 = metricSign === "neg" && "text-data-loss";
  const t10 = metricSign === "neutral" && "text-foreground-primary";
  let t11;
  if ($[16] !== t10 || $[17] !== t8 || $[18] !== t9) {
    t11 = cn("text-right tabular-nums text-body", t8, t9, t10);
    $[16] = t10;
    $[17] = t8;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== formatMetric || $[21] !== t11 || $[22] !== t7) {
    t12 = <AnimatedNumber value={t7} animateOnMount={true} format={formatMetric} className={t11} />;
    $[20] = formatMetric;
    $[21] = t11;
    $[22] = t7;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t6) {
    t13 = <span className="flex shrink-0 flex-col items-end gap-0.5">{t6}{t12}</span>;
    $[24] = t12;
    $[25] = t6;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== actionLabel || $[28] !== onClick || $[29] !== t13 || $[30] !== t2 || $[31] !== t5) {
    t14 = <button type="button" onClick={onClick} aria-label={actionLabel} className="flex min-w-0 flex-1 items-center gap-3 text-left">{t2}{t5}{t13}</button>;
    $[27] = actionLabel;
    $[28] = onClick;
    $[29] = t13;
    $[30] = t2;
    $[31] = t5;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== share) {
    t15 = share !== null && <ShareModal surface={share.surface} url={share.url} text={share.text} title={share.modalTitle} copyToast={{
      id: TOAST_IDS.RANK_SHARED,
      message: share.linkCopiedMessage
    }} trigger={<AnimatedButton variant="tertiary" size="sm" aria-label={share.ariaLabel} className="shrink-0"><Icon icon={LinkForwardIconSolid} className="size-4" /></AnimatedButton>} />;
    $[33] = share;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t14 || $[36] !== t15) {
    t16 = <Panel tone="accent" density="compact" className="gap-2! py-2! overflow-hidden bg-background-secondary/70 shadow-elevation-sticky supports-backdrop-filter:backdrop-blur-xl border border-border-primary"><PanelContent className="flex items-center gap-2 py-1">{t14}{t15}</PanelContent></Panel>;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  return t16;
}
interface CtaRowProps {
  tab: SocialTab;
  text: string;
  action: string;
}
function CtaRow(t0) {
  const $ = _c(9);
  const {
    tab,
    text,
    action
  } = t0;
  const href = tab === "creators" ? "/proposals?create=1" : tab === "boosters" || tab === "backers" ? "/proposals" : "/markets";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Icon icon={Award01Icon} aria-hidden={true} className="size-4 shrink-0 text-foreground-secondary" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== text) {
    t2 = <div className="flex min-w-0 items-center gap-2">{t1}<span className="truncate text-body text-foreground-secondary">{text}</span></div>;
    $[1] = text;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== action || $[4] !== href) {
    t3 = <AnimatedButton variant="primary" size="sm" asChild={true}><Link href={href}>{action}</Link></AnimatedButton>;
    $[3] = action;
    $[4] = href;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <Panel tone="accent" density="compact" className="gap-2! py-2! overflow-hidden bg-background-secondary/70 shadow-elevation-sticky supports-backdrop-filter:backdrop-blur-xl border border-border-primary"><PanelContent className="flex items-center justify-between gap-3 py-1">{t2}{t3}</PanelContent></Panel>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}